import { call, put, select, takeEvery, takeLatest } from 'typed-redux-saga'
import {
	errorHandler,
	notifyMessageCreator,
	ResponseError,
	translateErrorMessage,
	loadersSlice,
} from 'Modules'
import { ReceivingFileModel } from 'Models'
import { downloadFile } from 'Helpers'
import { Deserialize } from 'cerialize'
import {
	bgLimitRequestSelector,
	bgLimitRequestSlice,
	BgDocumentNs,
	bgDocumentSlice,
	bgRequestSlice,
	bgRequestSelector,
	bgDocumentApi,
	DocumentsForSendTypeTranslate,
	KindType,
	handleErrorWithForms,
} from 'Pages/ibro'

function* downloadDocument(action: BgDocumentNs.DownloadDocument) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { id, filename } = action.payload

		const res = yield* call(bgDocumentApi.downloadDocument, id)
		const url = window.URL.createObjectURL(res)
		downloadFile(url, filename)
		yield* call(notifyMessageCreator, 'common.document_upload.success')
	} catch (error) {
		yield* call(handleErrorWithForms, error as ResponseError)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* sendDocumentToEmail(action: BgDocumentNs.SendDocumentToEmail) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { id, type } = action.payload
		yield* call(bgDocumentApi.sendDocumentToEmail, id, type)
		const data = [DocumentsForSendTypeTranslate[type]]
		yield* call(notifyMessageCreator, 'broker.send_file_to_mail.success', { data })
	} catch (error) {
		yield* call(handleErrorWithForms, error as ResponseError)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* saveDocument(action: BgDocumentNs.SaveDocument) {
	try {
		const { data, id, callback } = action.payload
		yield* put(bgDocumentSlice.actions.addDocumentLoading(data.kind))
		const res = id
			? yield* call(bgDocumentApi.updateDocument, id, data)
			: yield* call(bgDocumentApi.sendDocument, data)
		const currentBg = yield* select(bgRequestSelector.selectCurrentBg)
		if (currentBg && currentBg.id === data.request_id) {
			yield* put(
				bgRequestSlice.actions.setItem({
					data: {
						...currentBg,
						[data.kind.toLowerCase()]: Deserialize(res, ReceivingFileModel),
					} as typeof currentBg,
				})
			)
		}
		callback?.()
	} catch (e) {
		const { errors } = Deserialize(e ?? {}, ResponseError)
		action.payload.errorCallback?.(translateErrorMessage(errors?.[0]))
	} finally {
		const { data } = action.payload
		yield* put(bgDocumentSlice.actions.removeDocumentLoading(data.kind))
	}
}

function* deleteDocument(action: BgDocumentNs.DeleteDocument) {
	try {
		const { id, fieldName = '', request_id } = action.payload
		if (fieldName && KindType[fieldName])
			yield* put(bgDocumentSlice.actions.addDocumentLoading(KindType[fieldName]))
		yield* call(bgDocumentApi.deleteDocument, id)
		const currentBg = yield* select(bgRequestSelector.selectCurrentBg)
		if (currentBg && currentBg.id === request_id) {
			yield* put(
				bgRequestSlice.actions.setItem({
					data: { ...currentBg, [fieldName]: null } as typeof currentBg,
				})
			)
		}
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		const { fieldName } = action.payload
		if (fieldName && KindType[fieldName])
			yield* put(bgDocumentSlice.actions.removeDocumentLoading(KindType[fieldName]))
	}
}

function* saveLimitRequestDocument(action: BgDocumentNs.SaveLimitRequestDocument) {
	try {
		const { data, id } = action.payload
		yield* put(bgDocumentSlice.actions.addDocumentLoading(data.kind))
		const res = id
			? yield* call(bgDocumentApi.updateDocument, id, data)
			: yield* call(bgDocumentApi.sendLimitRequestDocument, data)
		const currentLimitRequest = yield* select(bgLimitRequestSelector.selectCurrentLimitRequest)
		if (currentLimitRequest && currentLimitRequest.id === data.request_id) {
			yield* put(
				bgLimitRequestSlice.actions.setLimitRequest({
					data: {
						...currentLimitRequest,
						[data.kind.toLowerCase()]: Deserialize(res, ReceivingFileModel),
					} as typeof currentLimitRequest,
				})
			)
		}
	} catch (e) {
		const { errors } = Deserialize(e, ResponseError)
		action.payload.errorCallback?.(translateErrorMessage(errors[0]))
	} finally {
		const { data } = action.payload
		yield* put(bgDocumentSlice.actions.removeDocumentLoading(data.kind))
	}
}

function* deleteLimitRequestDocument(action: BgDocumentNs.DeleteLimitRequestDocument) {
	try {
		const { id, fieldName = '', request_id } = action.payload
		if (fieldName && KindType[fieldName])
			yield* put(bgDocumentSlice.actions.addDocumentLoading(KindType[fieldName]))
		yield* call(bgDocumentApi.deleteDocument, id)
		const currentLimitRequest = yield* select(bgLimitRequestSelector.selectCurrentLimitRequest)
		if (currentLimitRequest && currentLimitRequest.id === request_id) {
			yield* put(
				bgLimitRequestSlice.actions.setLimitRequest({
					data: { ...currentLimitRequest, [fieldName]: null } as typeof currentLimitRequest,
				})
			)
		}
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		const { fieldName } = action.payload
		if (fieldName && KindType[fieldName])
			yield* put(bgDocumentSlice.actions.removeDocumentLoading(KindType[fieldName]))
	}
}

function* downloadTrustDocument(action: BgDocumentNs.DownloadTrustDocument) {
	const { id, filename, request_id } = action.payload
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const res = yield* call(bgDocumentApi.downloadTrustDocument, request_id, id, filename)
		const url = window.URL.createObjectURL(res)
		downloadFile(url, filename)
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(notifyMessageCreator, 'common.document_upload.success')
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

export function* bgDocumentSaga() {
	yield* takeLatest(bgDocumentSlice.actions.downloadDocument.type, downloadDocument)
	yield* takeLatest(bgDocumentSlice.actions.sendDocumentToEmail.type, sendDocumentToEmail)

	yield* takeEvery(bgDocumentSlice.actions.saveDocument.type, saveDocument)
	yield* takeEvery(bgDocumentSlice.actions.deleteDocument.type, deleteDocument)

	yield* takeEvery(bgDocumentSlice.actions.saveLimitRequestDocument.type, saveLimitRequestDocument)
	yield* takeEvery(
		bgDocumentSlice.actions.deleteLimitRequestDocument.type,
		deleteLimitRequestDocument
	)
	yield* takeLatest(bgDocumentSlice.actions.downloadTrustDocument.type, downloadTrustDocument)
}
